import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import {
  RegistryChoice,
  RegistryModel,
  RegistryModelSearch,
  pickStyle,
  registryChoices,
  MatchList,
} from './RegistryModelSearch';
import { Badge, Button, Field, inputStyle, tokens } from './ui/controls';

type McapRow = {
  s3_uri: string;
  key: string;
  size_bytes?: number | null;
  fish_species: string[];
  fish_states_seen: string[];
  run_modes_seen: string[];
  calibration_was_run: boolean;
  camera_error_seen: boolean;
  cv_failure_seen: boolean;
  proportional_default_seen: boolean;
  proportional_default_packet_count: number;
};

type ModelSet = {
  name?: string;
  full?: { s3_uri?: string };
  lite?: { s3_uri?: string };
};

function record(value: unknown): Record<string, unknown> {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return { ...(value as Record<string, unknown>) };
  }
  return {};
}

function formatBytes(value: number) {
  if (value >= 1e9) return `${(value / 1e9).toFixed(1)} GB`;
  if (value >= 1e6) return `${Math.round(value / 1e6)} MB`;
  return `${value} B`;
}

function captureName(key: string) {
  const parts = key.split('/');
  return parts[parts.length - 1] || key;
}

export function McapJobForm({
  spec,
  onChange,
}: {
  spec: Record<string, unknown>;
  onChange: (spec: Record<string, unknown>) => void;
}) {
  const catalog = record(spec.catalog);
  const selectedUri = String(spec.mcap_path || '');
  const sets = Array.isArray(spec.model_sets) ? (spec.model_sets as ModelSet[]) : [];
  const [robots, setRobots] = useState<string[]>([]);
  const [robotQuery, setRobotQuery] = useState(String(catalog.robot || ''));
  const [robot, setRobot] = useState(String(catalog.robot || ''));
  const [robotError, setRobotError] = useState('');
  const [mcaps, setMcaps] = useState<McapRow[]>([]);
  const [mcapQuery, setMcapQuery] = useState('');
  const [mcapError, setMcapError] = useState('');
  const [choices, setChoices] = useState<RegistryChoice[]>([]);
  const [modelError, setModelError] = useState('');
  const [fullQuery, setFullQuery] = useState('');
  const [liteQuery, setLiteQuery] = useState('');
  const [full, setFull] = useState<RegistryChoice | null>(null);
  const [lite, setLite] = useState<RegistryChoice | null>(null);

  useEffect(() => {
    api.dataviewer.listMcapRobots()
      .then((res) => {
        setRobots(res.data.robots || []);
        setRobotError('');
      })
      .catch((err: any) => setRobotError(err.response?.data?.detail || err.message || 'Could not list MCAP robots'));
    api.dataviewer.modelRegistry()
      .then((res) => {
        setChoices(registryChoices((res.data as { models?: RegistryModel[] }).models || []));
        setModelError('');
      })
      .catch((err: any) => setModelError(err.response?.data?.detail || err.message || 'Could not list models'));
  }, []);

  useEffect(() => {
    if (!robot) {
      setMcaps([]);
      return;
    }
    api.dataviewer.listMcapCatalog(robot)
      .then((res) => {
        setMcaps(res.data.mcaps || []);
        setMcapError('');
      })
      .catch((err: any) => setMcapError(err.response?.data?.detail || err.message || 'Could not read the MCAP catalog'));
  }, [robot]);

  const robotMatches = useMemo(() => {
    const needle = robotQuery.trim().toLowerCase();
    const rows = needle ? robots.filter((name) => name.toLowerCase().includes(needle)) : robots;
    return rows.slice(0, 40);
  }, [robots, robotQuery]);

  const mcapMatches = useMemo(() => {
    const needle = mcapQuery.trim().toLowerCase();
    const rows = needle
      ? mcaps.filter((row) => {
        const haystack = [
          row.key,
          ...row.fish_species,
          ...row.fish_states_seen,
          ...row.run_modes_seen,
        ].join(' ').toLowerCase();
        return haystack.includes(needle);
      })
      : mcaps;
    return rows.slice(0, 40);
  }, [mcaps, mcapQuery]);

  const selected = mcaps.find((row) => row.s3_uri === selectedUri);

  const chooseMcap = (row: McapRow) => {
    onChange({
      ...spec,
      mcap_path: row.s3_uri,
      catalog: {
        robot,
        key: row.key,
        size_bytes: row.size_bytes,
        fish_species: row.fish_species,
        fish_states_seen: row.fish_states_seen,
        run_modes_seen: row.run_modes_seen,
        calibration_was_run: row.calibration_was_run,
        camera_error_seen: row.camera_error_seen,
        cv_failure_seen: row.cv_failure_seen,
        proportional_default_seen: row.proportional_default_seen,
        proportional_default_packet_count: row.proportional_default_packet_count,
      },
    });
  };

  const addSet = () => {
    if (!full || !lite) return;
    const name = `${full.label} + ${lite.label}`;
    onChange({
      ...spec,
      model_sets: [
        ...sets,
        {
          name,
          full: { source: 's3_uri', s3_uri: full.s3_uri },
          lite: { source: 's3_uri', s3_uri: lite.s3_uri },
        },
      ],
    });
    setFull(null);
    setLite(null);
  };

  const batchSize = spec.batch_size == null ? '16' : String(spec.batch_size);

  return (
    <div style={{ display: 'grid', gap: '0.9rem' }}>
      <div style={{ display: 'grid', gap: '0.45rem' }}>
        <Field label="Robot">
          <input
            value={robotQuery}
            onChange={(event) => setRobotQuery(event.target.value)}
            placeholder="Search robot, for example PSDN-S12"
            style={inputStyle}
          />
        </Field>
        {robotError ? (
          <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{robotError}</span>
        ) : (
          <MatchList empty="No robots match that search.">
            {robotMatches.map((name) => (
              <button key={name} type="button" onClick={() => { setRobot(name); setRobotQuery(name); }} style={pickStyle(name === robot)}>
                {name}
              </button>
            ))}
          </MatchList>
        )}
      </div>

      {robot && (
        <div style={{ display: 'grid', gap: '0.45rem' }}>
          <Field label="MCAP">
            <input
              value={mcapQuery}
              onChange={(event) => setMcapQuery(event.target.value)}
              placeholder="Search capture, species, state, or run mode"
              style={inputStyle}
            />
          </Field>
          <span style={{ fontSize: '0.8rem', color: tokens.textMuted }}>
            Catalog metadata for {robot} from mcaps/_catalog/{robot}/run_summary.jsonl.
          </span>
          {mcapError ? (
            <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{mcapError}</span>
          ) : (
            <MatchList empty="No MCAPs match that search.">
              {mcapMatches.map((row) => (
                <button key={row.s3_uri} type="button" onClick={() => chooseMcap(row)} style={pickStyle(row.s3_uri === selectedUri)}>
                  <div>{captureName(row.key)}{row.size_bytes ? ` · ${formatBytes(row.size_bytes)}` : ''}</div>
                  <div style={{ color: tokens.textMuted, marginTop: '0.2rem' }}>
                    {(row.fish_species || []).join(', ') || 'no species'}
                    {' · '}
                    {(row.fish_states_seen || []).join(', ') || 'no fish states'}
                    {' · '}
                    {(row.run_modes_seen || []).join(', ') || 'no run mode'}
                  </div>
                </button>
              ))}
            </MatchList>
          )}
        </div>
      )}

      {(selected || selectedUri) && (
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem' }}>{selected ? captureName(selected.key) : selectedUri}</span>
          {(selected?.fish_species || []).map((name) => <Badge key={name} tone="info">{name}</Badge>)}
          {(selected?.run_modes_seen || []).map((name) => <Badge key={name}>{name}</Badge>)}
          {selected?.calibration_was_run ? <Badge tone="warning">calibration</Badge> : null}
          {selected?.camera_error_seen ? <Badge tone="danger">camera error</Badge> : null}
          {selected?.cv_failure_seen ? <Badge tone="danger">cv failure</Badge> : null}
          {selected?.proportional_default_seen ? <Badge>proportional default {selected.proportional_default_packet_count}</Badge> : null}
        </div>
      )}

      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.textMuted }}>Full model</span>
        <RegistryModelSearch
          choices={choices}
          error={modelError}
          query={fullQuery}
          onQuery={setFullQuery}
          selectedUri={full?.s3_uri || ''}
          onSelect={setFull}
          placeholder="Search full model"
        />
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.textMuted }}>Lite model</span>
        <RegistryModelSearch
          choices={choices}
          error=""
          query={liteQuery}
          onQuery={setLiteQuery}
          selectedUri={lite?.s3_uri || ''}
          onSelect={setLite}
          placeholder="Search lite model"
        />
        <div>
          <Button onClick={addSet} disabled={!full || !lite}>Add model set</Button>
        </div>
        {sets.length === 0 ? (
          <span style={{ fontSize: '0.8rem', color: tokens.textMuted }}>
            Add at least one full and lite pair. Each set is run on the selected MCAP.
          </span>
        ) : (
          <MatchList empty="">
            {sets.map((item, index) => (
              <button
                key={`${item.name}-${index}`}
                type="button"
                onClick={() => onChange({ ...spec, model_sets: sets.filter((_, itemIndex) => itemIndex !== index) })}
                style={pickStyle(false)}
              >
                {item.name || `Set ${index + 1}`} · remove
              </button>
            ))}
          </MatchList>
        )}
      </div>

      <Field label="Batch size">
        <input
          value={batchSize}
          onChange={(event) => {
            const value = event.target.value.trim();
            onChange({ ...spec, batch_size: value === '' ? undefined : Number(value) });
          }}
          style={inputStyle}
        />
      </Field>
    </div>
  );
}
